import { http } from "./client";

// Companies, branches, departments, roles, job titles and payroll groups (clients).
// Each function returns the response body (same data the old Redux thunks returned).
const get = (url, config) => http.get(url, config).then((r) => r.data);
const post = (url, body, config) =>
  http.post(url, body, config).then((r) => r.data);
const patch = (url, body) => http.patch(url, body).then((r) => r.data);
const del = (url) => http.delete(url).then((r) => r.data);

export const companyApi = {
  // All companies the user can see (descendants)
  // 404 = no companies yet → []
  getDescendants: () =>
    get("/companies/descendant").catch((err) => {
      if (err?.response?.status === 404) return [];
      throw err;
    }),
  // Company + its sub-companies
  getHierarchy: (companyId) => get(`/companies/companyhierarchy/${companyId}`),
  // Tree for the Companies screen → { data: [node with children, counts, details summary] }
  getTree: () => get("/org-chart/companies"),
  getById: (id) => get(`/companies/${id}`),
  // { companyName, type, region, parentId (null = top level, super admin only), isActive }
  create: (data) => post("/companies", data),
  update: (id, data) => patch(`/companies/${id}`, data),
  // 409 while still in use (deactivate instead); never your own company
  remove: (id) => del(`/companies/${id}`),
  getDetails: (id) => get(`/companies/${id}/details`),
  // { taxId, registrationNumber, mainContact }
  saveDetails: (id, data) =>
    http.put(`/companies/${id}/details`, data).then((r) => r.data),
  getAddresses: (id) => get(`/companies/${id}/addresses`),
  // { type: Main|Billing|Shipping, addressLine1, addressLine2?, city, province, state?, postalCode, phoneNumber?, faxNumber?, email?, supportEmail?, isPrimary?, isActive? }
  addAddress: (id, data) => post(`/companies/${id}/addresses`, data),
  updateAddress: (addressId, data) =>
    patch(`/companies/addresses/${addressId}`, data),
  removeAddress: (addressId) => del(`/companies/addresses/${addressId}`),
};

export const branchApi = {
  // Every branch the user may see (super admin: all companies)
  getAll: () => get("/companies/branch"),
  getByCompany: (companyId) =>
    get(`/companies/branches/by-company/${companyId}`),
  // Branch + all sub-branches, as a flat array
  getHierarchy: (branchId) => get(`/companies/hierarchy/${branchId}`),
  // → { …, branchManager: { id, employeeId, firstName, lastName } | null }
  getById: (id) => get(`/companies/branch/${id}`),
  // body may include branchManagerId (uuid, or null to remove); names are unique per company
  create: (data) => post("/companies/branch", data),
  update: (id, data) => patch(`/companies/branch/${id}`, data),
  // 409 while used or while it has sub-branches (deactivate instead)
  remove: (id) => del(`/companies/branch/${id}`),
  // PATCH /companies/branch/:id/location { latitude, longitude, radius } (radius 20–5000 m, default 200;
  // latitude/longitude null removes the location). Used by the check-in radius rule.
  setLocation: (id, { latitude, longitude, radius }) =>
    patch(`/companies/branch/${id}/location`, {
      latitude,
      longitude,
      ...(radius !== undefined && { radius }),
    }),
};

// Departments are per company: companyId null = shared by every company.
export const departmentApi = {
  // Shared + your company tree, or shared + the given company; active=true|false filters
  getAll: (companyId, { active } = {}) =>
    get("/companies/department", {
      params: {
        ...(companyId ? { companyId } : {}),
        ...(active !== undefined ? { active: String(active) } : {}),
      },
    }),
  create: (data) =>
    post("/companies/department", {
      departmentName: data.departmentName,
      description: data.description,
      isActive: data.isActive,
      companyId: data.companyId || null,
    }),
  update: (id, data) =>
    patch(`/companies/department/${id}`, {
      departmentName: data.departmentName,
      description: data.description,
      isActive: data.isActive,
      ...(data.companyId !== undefined && {
        companyId: data.companyId || null,
      }),
    }),
  // 409 while still used (deactivate instead)
  remove: (id) => del(`/companies/department/${id}`),
  toggleStatus: (id, currentStatus) =>
    patch(`/companies/department/${id}`, { isActive: !currentStatus }),
};

export const roleApi = {
  // ?companyId → shared roles + that company's (must be in your tree)
  getAll: (companyId) =>
    get("/companies/roles", companyId ? { params: { companyId } } : undefined),
  create: (data) => post("/companies/roles", data),
  update: (id, data) => patch(`/companies/roles/${id}`, data),
  remove: (id) => del(`/companies/roles/${id}`),
};

export const jobTitleApi = {
  getAll: () => get("/job-title"),
  // Empty values are dropped before sending
  create: (data) =>
    post(
      "/job-title",
      Object.fromEntries(
        Object.entries(data).filter(
          ([, v]) => v !== "" && v !== null && v !== undefined,
        ),
      ),
    ),
  update: (id, data) => patch(`/job-title/${id}`, data),
  toggleStatus: (id) => patch(`/job-title/${id}/toggle`),
};

// UI field `clientName` maps to API field `name`; isActive maps to status.
const toClientPayload = (data) => ({
  name: data.clientName,
  clientCode: data.clientCode,
  details: data.details,
  address: data.address,
  mobile: data.mobile,
  email: data.email,
  contactPerson: data.contactPerson,
  website: data.website,
  status: data.isActive ? "Active" : "Inactive",
});

export const clientApi = {
  getAll: () => get("/client-codes"),
  create: (data) => post("/client-codes", toClientPayload(data)),
  update: (id, data) => patch(`/client-codes/${id}`, toClientPayload(data)),
  toggleStatus: (id, currentStatus) =>
    patch(`/client-codes/${id}`, {
      status: currentStatus === "Active" ? "Inactive" : "Active",
    }),
};
